/**
 * Row primitives shared by every Premium Selection card:
 *
 *   • `FieldRow`    — label (+ info tooltip) on the left, an input
 *                     control on the right. Wrap the control with
 *                     `useBlurFlash` to get the blue commit flash.
 *   • `ComputedRow` — label (+ tooltip) left, auto-calculated value
 *                     right. Skeleton while the recalc is pending;
 *                     the resolved value slides/fades in (the `key`
 *                     remount retriggers the CSS animation). The
 *                     `highlighted` variant is the gray full-bleed
 *                     row (Adjusted Premium, Total/Final Premium).
 *   • `InfoTip`     — the ⓘ trigger used by both.
 *
 * Rows separate with dashed hairlines per Figma — the parent renders
 * them inside `<CardRows>`.
 */
import type { ReactNode } from "react";
import { AlertCircle, InfoCircle } from "@untitledui/icons";
import { Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { Skeleton } from "../../../../components/skeleton";
import type { FieldTooltip } from "../tooltips";

export function InfoTip({ tooltip }: { tooltip: FieldTooltip }) {
    return (
        <Tooltip title={tooltip.title} description={tooltip.formula} placement="top">
            <TooltipTrigger aria-label="More info" className="cursor-help align-middle">
                <InfoCircle aria-hidden="true" className="text-fg-quaternary size-4" />
            </TooltipTrigger>
        </Tooltip>
    );
}

interface FieldRowProps {
    label: string;
    tooltip?: FieldTooltip;
    /** Bump to retrigger the commit flash (from `useBlurFlash`). */
    flashKey?: number;
    /** Width cap for the control column. Defaults to the Figma's
     *  compact input width. */
    controlClassName?: string;
    children: ReactNode;
    /** Custom attribute for E2E testing — applied to the row's outer `<div>`. */
    "data-autotest"?: string;
}

export function FieldRow({ label, tooltip, flashKey = 0, controlClassName, children, "data-autotest": dataAutotest }: FieldRowProps) {
    return (
        <div data-autotest={dataAutotest} className="flex min-h-15 items-center justify-between gap-4 py-3">
            <span className="text-secondary inline-flex items-center gap-1.5 text-sm font-medium">
                {label}
                {tooltip && <InfoTip tooltip={tooltip} />}
            </span>
            <div key={flashKey} className={cx("w-40 shrink-0 rounded-lg", flashKey > 0 && "field-flash", controlClassName)}>
                {children}
            </div>
        </div>
    );
}

interface ComputedRowProps {
    label: string;
    tooltip?: FieldTooltip;
    /** Pre-formatted display value ("—" when not computable). */
    value: string;
    /** True while a recalc affecting this field is in flight. */
    isPending?: boolean;
    /** Gray full-bleed emphasis row (walk results). */
    highlighted?: boolean;
    /** When true, the value renders in the error color with a leading alert
     *  icon and `alertTooltip` on hover (threshold breach — OR-1358). */
    alert?: boolean;
    alertTooltip?: string;
    /** Custom attribute for E2E testing — applied to the row's outer `<div>`. */
    "data-autotest"?: string;
}

export function ComputedRow({
    label,
    tooltip,
    value,
    isPending,
    highlighted,
    alert,
    alertTooltip,
    "data-autotest": dataAutotest,
}: ComputedRowProps) {
    return (
        <div
            data-autotest={dataAutotest}
            className={cx(
                "min-h-15 flex items-center justify-between gap-4 py-3",
                highlighted && "bg-secondary -mx-4 rounded-lg px-4 sm:-mx-6 sm:px-6",
            )}
        >
            <span className="text-secondary inline-flex items-center gap-1.5 text-sm font-medium">
                {label}
                {tooltip && <InfoTip tooltip={tooltip} />}
            </span>
            {isPending ? (
                <Skeleton className="h-5 w-24" />
            ) : alert ? (
                <Tooltip title={alertTooltip} placement="top">
                    <TooltipTrigger aria-label={alertTooltip} className="inline-flex cursor-help items-center gap-1.5">
                        <AlertCircle aria-hidden="true" className="text-fg-error-primary size-4" />
                        <span key={value} className="text-error-primary value-resolve text-sm font-semibold">
                            {value}
                        </span>
                    </TooltipTrigger>
                </Tooltip>
            ) : (
                <span key={value} className="text-primary value-resolve text-sm font-semibold">
                    {value}
                </span>
            )}
        </div>
    );
}

/** Stacks rows with the dashed hairline separators from the Figma, with a
 *  small uniform gap so the cards breathe at the Figma's vertical rhythm. */
export function CardRows({ children }: { children: ReactNode }) {
    return <div className="[&>*+*]:border-secondary flex flex-col gap-y-1 [&>*+*]:border-t [&>*+*]:border-dashed">{children}</div>;
}
